// Host for confirmDialog() (stores/confirm.ts). Mounted once in main.tsx;
// renders the request at the head of the queue.

import { useRef } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { useConfirmStore, type ConfirmRequest } from '../stores/confirm';
import { useDialogFocus } from '../lib/dialog-focus';
import styles from './ConfirmDialog.module.css';

export function ConfirmDialog() {
  const request = useConfirmStore((s) => s.queue[0]);
  if (!request) return null;
  // Keyed by id so each request mounts fresh: focus lands on its Cancel button.
  return <ConfirmView key={request.id} request={request} />;
}

function ConfirmView({ request }: { request: ConfirmRequest }) {
  const settle = useConfirmStore((s) => s.settle);
  const ref = useRef<HTMLDivElement>(null);
  const cancel = () => settle(request.id, false);
  useDialogFocus(ref, cancel);

  const danger = request.tone === 'danger';
  const titleId = `confirm-title-${request.id}`;
  const messageId = `confirm-message-${request.id}`;
  const paragraphs = request.message?.split(/\n\s*\n/) ?? [];

  return (
    <div
      className={styles.backdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) cancel();
      }}
    >
      <div
        ref={ref}
        className={styles.dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={paragraphs.length ? messageId : undefined}
        data-tone={request.tone}
        tabIndex={-1}
      >
        <div className={styles.icon} aria-hidden="true">
          {danger ? <AlertTriangle size={20} /> : <HelpCircle size={20} />}
        </div>
        <div className={styles.body}>
          <h2 id={titleId} className={styles.title}>{request.title}</h2>
          {paragraphs.length > 0 && (
            <div id={messageId} className={styles.message}>
              {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          )}
        </div>
        <div className={styles.actions}>
          {/* Cancel first: it takes the initial focus, so a stray Enter is safe. */}
          <button type="button" className={styles.cancel} onClick={cancel}>
            {request.cancelLabel}
          </button>
          <button
            type="button"
            className={danger ? styles.confirmDanger : styles.confirm}
            onClick={() => settle(request.id, true)}
          >
            {request.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
